/**
 * Turns a stream of raw per-frame ZXing detections into confirmed,
 * deduplicated barcode events (spec §16-18): a barcode must be seen
 * `requiredHits` times in a row within `detectionWindowMs` before it is
 * CONFIRMED, and a barcode already confirmed within `cooldownMs` is
 * SUPPRESSED so the same product in front of the camera doesn't trigger a
 * new backend lookup every frame.
 */
export interface BarcodeStabilizerOptions {
  requiredHits: number;
  detectionWindowMs: number;
  cooldownMs: number;
}

export type StabilizerOutcome = "PENDING" | "CONFIRMED" | "SUPPRESSED";

const DEFAULT_OPTIONS: BarcodeStabilizerOptions = {
  requiredHits: 3,
  detectionWindowMs: 1000,
  cooldownMs: 2_000,
};

export class BarcodeStabilizer {
  private readonly opts: BarcodeStabilizerOptions;
  private streak: { barcode: string; firstSeenAt: number; count: number } | null = null;
  private readonly lastConfirmedAt = new Map<string, number>();

  constructor(opts: Partial<BarcodeStabilizerOptions> = {}) {
    this.opts = { ...DEFAULT_OPTIONS, ...opts };
  }

  /** Feed one raw per-frame detection. Returns what the caller should do with it. */
  observe(barcode: string, now: number): StabilizerOutcome {
    const lastConfirmed = this.lastConfirmedAt.get(barcode);
    if (lastConfirmed !== undefined && now - lastConfirmed < this.opts.cooldownMs) {
      this.streak = null;
      return "SUPPRESSED";
    }

    if (!this.streak || this.streak.barcode !== barcode || now - this.streak.firstSeenAt > this.opts.detectionWindowMs) {
      this.streak = { barcode, firstSeenAt: now, count: 1 };
    } else {
      this.streak.count += 1;
    }

    if (this.streak.count < this.opts.requiredHits) {
      return "PENDING";
    }

    this.lastConfirmedAt.set(barcode, now);
    this.streak = null;
    return "CONFIRMED";
  }

  /** Camera moved off the barcode (no detection this frame) — resets the in-progress streak only. */
  reset(): void {
    this.streak = null;
  }

  /** Clears all recorded confirmation timestamps (e.g. on manual reset or scan session restart). */
  clearHistory(): void {
    this.lastConfirmedAt.clear();
    this.streak = null;
  }

  /** Returns active stabilizer configuration. */
  getOptions(): Readonly<BarcodeStabilizerOptions> {
    return this.opts;
  }
}
